import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Mail, Edit3, Save, X } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { adminListEmailMessages, adminListEmailTemplates, adminUpdateEmailMessage } from '../../services/api';
import AdminDashboardNav from '../../components/dashboard/AdminDashboardNav';

interface EmailMessage {
  id: number;
  key: string;
  label: string;
  subject: string;
  templateId: number;
  availableVariables: string | null;
  senderType: string;
  createdAt: number;
  updatedAt: number;
}

interface EmailTemplate {
  id: number;
  name: string;
}

export default function AdminMessagesPage() {
  const { token } = useAuthStore();
  const [messages, setMessages] = useState<EmailMessage[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<EmailMessage>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const [msgRes, tplRes] = await Promise.all([
        adminListEmailMessages(token!),
        adminListEmailTemplates(token!),
      ]);
      if (msgRes.ok) {
        const json = await msgRes.json() as { data: EmailMessage[] };
        setMessages(json.data);
      }
      if (tplRes.ok) {
        const json = await tplRes.json() as { data: EmailTemplate[] };
        setTemplates(json.data);
      }
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [token]);

  const startEdit = (msg: EmailMessage) => {
    setEditingId(msg.id);
    setEditForm({ subject: msg.subject, templateId: msg.templateId, senderType: msg.senderType });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSave = async (id: number) => {
    setSaving(true);
    setError('');
    try {
      const res = await adminUpdateEmailMessage(id, editForm, token!);
      if (res.ok) {
        setEditingId(null);
        setEditForm({});
        await fetchData();
      } else {
        setError('Failed to save');
      }
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getTemplateName = (id: number) => templates.find(t => t.id === id)?.name || '—';

  const senderBadge = (type: string) => {
    if (type === 'artist') return <span className="px-2 py-0.5 rounded text-[10px] font-mono uppercase bg-electric-cyan/10 text-electric-cyan border border-electric-cyan/30">Artist → Fan</span>;
    return <span className="px-2 py-0.5 rounded text-[10px] font-mono uppercase bg-purple-500/10 text-purple-400 border border-purple-500/30">System → Artist</span>;
  };

  return (
    <div className="min-h-screen bg-deep-charcoal text-white">
      <AdminDashboardNav currentPage="messages" />
      <div className="max-w-5xl mx-auto px-6 pt-24 pb-8">
        <div className="flex items-center gap-4 mb-8">
          <h1 className="text-2xl font-bold">Email Messages</h1>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={24} className="animate-spin text-electric-cyan" />
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="glass-card p-4"
              >
                {editingId === msg.id ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail size={16} className="text-electric-cyan" />
                      <span className="font-medium text-sm">{msg.label}</span>
                      <span className="text-[10px] font-mono text-text-secondary">({msg.key})</span>
                    </div>
                    <div>
                      <label className="text-xs text-text-secondary block mb-1">Subject (supports {"{{tokens}}"}):</label>
                      <input
                        type="text"
                        value={editForm.subject || ''}
                        onChange={(e) => setEditForm(f => ({ ...f, subject: e.target.value }))}
                        className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-electric-cyan"
                      />
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="text-xs text-text-secondary block mb-1">Template:</label>
                        <select
                          value={editForm.templateId || ''}
                          onChange={(e) => setEditForm(f => ({ ...f, templateId: parseInt(e.target.value) }))}
                          className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-electric-cyan"
                        >
                          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-text-secondary block mb-1">Sender type:</label>
                        <select
                          value={editForm.senderType || 'system'}
                          onChange={(e) => setEditForm(f => ({ ...f, senderType: e.target.value }))}
                          className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-electric-cyan"
                        >
                          <option value="system">System → Artist</option>
                          <option value="artist">Artist → Fan</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button onClick={cancelEdit} className="px-3 py-1.5 text-xs text-text-secondary hover:text-white transition-colors flex items-center gap-1">
                        <X size={14} /> Cancel
                      </button>
                      <button
                        onClick={() => handleSave(msg.id)}
                        disabled={saving}
                        className="btn-primary !px-4 !py-1.5 text-xs flex items-center gap-1 disabled:opacity-50"
                      >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Mail size={18} className="text-electric-cyan mt-0.5" />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-sm">{msg.label}</p>
                          {senderBadge(msg.senderType)}
                        </div>
                        <p className="text-xs text-text-secondary mb-1">
                          <span className="text-white/60">Subject:</span> {msg.subject}
                        </p>
                        <p className="text-xs text-text-secondary mb-1">
                          <span className="text-white/60">Template:</span> {getTemplateName(msg.templateId)}
                        </p>
                        {msg.availableVariables && (
                          <p className="text-[11px] text-text-secondary font-mono mt-1 opacity-70">
                            Variables: {msg.availableVariables}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => startEdit(msg)}
                      className="p-1.5 text-text-secondary hover:text-electric-cyan transition-colors"
                    >
                      <Edit3 size={16} />
                    </button>
                  </div>
                )}
              </motion.div>
            ))}
            {messages.length === 0 && (
              <p className="text-center text-text-secondary py-8">No email messages configured.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
